import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';
import Link from '../../Link';
import PeriodFilter from '../shared/PeriodFilter';
import KpiCard from '../shared/KpiCard';
import InfoTooltip from '../shared/InfoTooltip';
import RevenueBreakdownDonut from '../shared/RevenueBreakdownDonut';
import EvolutionChart from '../shared/EvolutionChart';
import { LoadingSkeleton, ErrorState, MessageState } from '../shared/ReportingStates';
import { formatMoney, formatCount } from '../shared/reportingFormat';
import { deriveEarningsView, PAGE_SIZE, DEFAULT_STATE } from '../shared/reportingUrlState';
import EarningsTable from './EarningsTable';
import EarningDetail, { TRANSACTIONS_PATH } from './EarningDetail';
import messages from '../../../locale/jsonMessages';
import sh from '../shared/OwnerReporting.css';
import s from './OwnerEarnings.css';

export const ADD_VEHICLE_PATH = '/become-a-owner';
const SEARCH_DELAY_MS = 400;

const TABS = [
  { key: 'all', label: 'orpTabAll', help: 'orpHelpTabAll' },
  { key: 'upcoming', label: 'orpTabUpcoming' },
  { key: 'pendingPayout', label: 'orpTabPending' },
  { key: 'paid', label: 'orpTabPaid' },
];
const CARDS = [
  { key: 'upcoming', tab: 'upcoming', name: 'orpCardUpcoming', hint: 'orpCardUpcomingHint', help: 'orpHelpUpcoming', dot: 'dotUpcoming' },
  { key: 'pendingPayout', tab: 'pendingPayout', name: 'orpCardPending', hint: 'orpCardPendingHint', help: 'orpHelpPending', dot: 'dotPending' },
  { key: 'paid', tab: 'paid', name: 'orpCardPaid', hint: 'orpCardPaidHint', help: 'orpHelpPaid', dot: 'dotPaid' },
];
const COMPARE_LABELS = { today: 'orpVsToday', '7d': 'orpVs7d', '30d': 'orpVs30d', year: 'orpVsYear' };

// Compact list of page numbers: first, last and the neighbours of the current page (null = gap).
export function pageWindow(page, pageCount) {
  const wanted = new Set([1, pageCount, page - 1, page, page + 1]);
  const pages = [...wanted].filter((value) => value >= 1 && value <= pageCount).sort((a, b) => a - b);
  const out = [];
  pages.forEach((value, index) => {
    if (index > 0 && value - pages[index - 1] > 1) out.push(null);
    out.push(value);
  });
  return out;
}

const SearchBox = ({ value, onSearch }) => {
  const { formatMessage } = useIntl();
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { setDraft(value || ''); }, [value]);
  useEffect(() => {
    if (draft.trim() === (value || '')) return undefined;
    const timer = setTimeout(() => onSearch(draft.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [draft]);
  return (
    <input type="search" className={s.searchInput} value={draft} maxLength={100}
      placeholder={formatMessage(messages.orpSearchPlaceholder)} aria-label={formatMessage(messages.orpSearchLabel)}
      onChange={(event) => setDraft(event.target.value)} />
  );
};

// "Revenue & payouts" page. Everything displayed (cards, donut, series, lines) is what getOwnerEarnings returns; the page
// only formats it. State comes from the URL (parseEarningsQuery) and changes go through onChange(patch).
// Props: state, loading, networkError, earnings (query result), vehicles [{ id, title }], statementUrl, onChange, onRetry, onReset.
const EarningsPage = ({ state, loading, networkError, earnings, statementUrl, onChange, onRetry, onReset }) => {
  const { formatMessage, locale } = useIntl();
  const [selected, setSelected] = useState(null);
  const [opener, setOpener] = useState(null);
  const openDetail = (line, trigger) => { setOpener(trigger || null); setSelected(line); };
  // Vehicles come from the server (getOwnerEarnings.vehicles); an untitled vehicle gets a translated fallback.
  const vehicles = ((earnings && earnings.vehicles) || []).map((vehicle) => ({
    id: vehicle.listId, title: vehicle.title || formatMessage(messages.orpVehicleFallback, { id: vehicle.listId }),
  }));
  // ?page=999: the page does not exist any more -> back to the last page (the URL is replaced, no history entry).
  const outOfRange = Boolean(earnings && earnings.status === 'success' && Number(earnings.count) > 0
    && (earnings.rows || []).length === 0 && state.page > 1);
  useEffect(() => {
    if (outOfRange && Number(earnings.pageCount) >= 1) onChange({ page: Number(earnings.pageCount) }, { replace: true });
  }, [outOfRange, earnings && earnings.pageCount]);
  const view = deriveEarningsView({ loading, networkError, earnings, state });
  const currency = earnings && earnings.convertCurrency;
  const refreshing = Boolean(loading && earnings);
  const closeDetail = React.useCallback(() => setSelected(null), []);

  const header = (
    <div className={sh.header}>
      <div className={sh.headerText}>
        <h1 className={sh.pageTitle}>{formatMessage(messages.orpEarningsTitle)}</h1>
        <p className={sh.pageSubtitle}>{formatMessage(messages.orpEarningsSubtitle)}</p>
        {/* the former "Transactions" page stays reachable from here (visible link, not only from the detail panel) */}
        {view !== 'notLoggedIn' && (
          <p className={s.transactionsLinkBox}>
            <Link to={TRANSACTIONS_PATH} className={s.transactionsLink}>{formatMessage(messages.ormTransactionsLink)}</Link>
          </p>
        )}
      </div>
      {view !== 'notLoggedIn' && (
        <PeriodFilter period={state.period} startDate={state.startDate} endDate={state.endDate} listId={state.listId}
          vehicles={vehicles} onChange={onChange} />
      )}
    </div>
  );

  let body;
  if (view === 'loading' || outOfRange) {
    body = <LoadingSkeleton />;
  } else if (view === 'notLoggedIn') {
    body = <MessageState title={formatMessage(messages.orpNotOwnerTitle)} text={(earnings && earnings.errorMessage) || formatMessage(messages.orpNotOwnerText)} />;
  } else if (view === 'error') {
    body = <ErrorState message={earnings && earnings.errorMessage} onRetry={onRetry} />;
  } else if (view === 'empty') {
    // Without any vehicle: invite to add one. With vehicles: simply no reservation in the period.
    body = vehicles.length === 0
      ? (
        <MessageState title={formatMessage(messages.orpEmptyNoVehicleTitle)} text={formatMessage(messages.orpEmptyNoVehicleText)}>
          <Link to={ADD_VEHICLE_PATH} className={sh.primaryButton}>{formatMessage(messages.orpAddVehicle)}</Link>
        </MessageState>
      )
      : <MessageState title={formatMessage(messages.orpEmptyTitle)} text={formatMessage(messages.orpEmptyText)} />;
  } else {
    const { summary = {}, revenue = {}, breakdown, series, rows = [], period = {} } = earnings;
    const compareKey = COMPARE_LABELS[period.preset];
    const compare = compareKey ? formatMessage(messages[compareKey]) : formatMessage(messages.orpVsCustom, { days: period.previousDays });
    const pageCount = Number(earnings.pageCount) || 1;
    const page = Number(earnings.page) || state.page;
    const noRows = view === 'noResults';
    body = (
      <div className={cx(refreshing && sh.refreshing)} aria-busy={refreshing}>
        <div className={sh.cardGrid}>
          <KpiCard accent label={formatMessage(messages.orpKpiNetRevenue)} help={formatMessage(messages.orpHelpNetRevenue)}
            value={formatMoney(summary.netRevenue && summary.netRevenue.value, currency, locale)} indicator={summary.netRevenue} compareLabel={compare} />
          <KpiCard label={formatMessage(messages.orpKpiVolume)} help={formatMessage(messages.orpHelpVolume)}
            value={formatMoney(summary.volume && summary.volume.value, currency, locale)} indicator={summary.volume} compareLabel={compare} />
          <KpiCard label={formatMessage(messages.orpKpiOptions)} help={formatMessage(messages.orpHelpOptions)}
            value={formatMoney(summary.optionsAmount && summary.optionsAmount.value, currency, locale)} indicator={summary.optionsAmount} compareLabel={compare} />
        </div>
        <div className={sh.panelGrid}>
          <RevenueBreakdownDonut breakdown={breakdown} total={summary.volume && summary.volume.value} currency={currency} help={formatMessage(messages.orpHelpBreakdown)} />
          <EvolutionChart series={series} granularity={state.granularity} currency={currency}
            help={formatMessage(messages.orpHelpEvolution)} onGranularityChange={(granularity) => onChange({ granularity })} />
        </div>

        <div className={sh.panel}>
          <div className={s.tabs} role="group" aria-label={formatMessage(messages.orpTabsLabel)}>
            {TABS.map((tab) => (
              <span key={tab.key} className={s.tabItem}>
                <button type="button" aria-pressed={state.tab === tab.key}
                  className={cx(s.tab, state.tab === tab.key && s.tabActive)} onClick={() => state.tab !== tab.key && onChange({ tab: tab.key })}>
                  {formatMessage(messages[tab.label])}
                </button>
                {tab.help && <InfoTooltip text={formatMessage(messages[tab.help])} />}
              </span>
            ))}
          </div>
          <div className={s.toolbar}>
            <div className={s.statusCards}>
              {CARDS.map((card) => (
                <div key={card.key} className={s.statusCardBox}>
                  <button type="button" aria-pressed={state.tab === card.tab}
                    className={cx(s.statusCard, state.tab === card.tab && s.statusCardActive)}
                    onClick={() => onChange({ tab: state.tab === card.tab ? DEFAULT_STATE.tab : card.tab })}>
                    <span className={cx(s.statusDot, s[card.dot])} aria-hidden="true" />
                    <span className={s.statusBody}>
                      <span className={s.statusName}>{formatMessage(messages[card.name])}</span>
                      <span className={s.statusAmount}>{formatMoney(revenue[card.key], currency, locale)}</span>
                      <span className={s.statusHint}>{formatMessage(messages[card.hint])}</span>
                    </span>
                    <span className={s.statusArrow} aria-hidden="true">&rsaquo;</span>
                  </button>
                  {/* sibling of the button (never inside it): clicking "i" must not change the filter */}
                  <span className={s.statusInfo}><InfoTooltip text={formatMessage(messages[card.help])} /></span>
                </div>
              ))}
            </div>
            <div className={s.exportBox}>
              <a className={sh.secondaryButton} href={statementUrl} download>{formatMessage(messages.orpExport)}</a>
            </div>
          </div>

          <div className={s.tableHead}>
            <h2 className={s.tableTitle}>{formatMessage(messages.orpTableTitle)}</h2>
            <SearchBox value={state.search} onSearch={(search) => onChange({ search })} />
          </div>

          {noRows
            ? (
              <MessageState title={formatMessage(messages.orpNoResultsTitle)} text={formatMessage(messages.orpNoResultsText)}>
                <button type="button" className={sh.secondaryButton} onClick={onReset}>{formatMessage(messages.orpResetFilters)}</button>
              </MessageState>
            )
            : (
              <>
                <EarningsTable rows={rows} sortBy={state.sortBy} sortDir={state.sortDir} currency={currency}
                  onSort={(column) => onChange(state.sortBy === column
                    ? { sortDir: state.sortDir === 'asc' ? 'desc' : 'asc' }
                    : { sortBy: column, sortDir: column === 'checkIn' || column === 'grossAmount' || column === 'netRevenue' ? 'desc' : 'asc' })}
                  onView={openDetail} />
                <div className={s.pagination}>
                  <span className={s.resultCount}>{formatMessage(messages.orpResultCount, { count: formatCount(earnings.count, locale), page, pageCount })}</span>
                  {pageCount > 1 && (
                    <nav className={s.pages} aria-label={formatMessage(messages.orpPagination)}>
                      <button type="button" className={s.pageButton} disabled={page <= 1} onClick={() => onChange({ page: page - 1 })}
                        aria-label={formatMessage(messages.orpPrev)}>&lsaquo;</button>
                      {pageWindow(page, pageCount).map((value, index) => (value === null
                        ? <span key={`gap-${index}`} className={s.resultCount}>&hellip;</span>
                        : (
                          <button key={value} type="button" aria-current={value === page ? 'page' : undefined}
                            className={cx(s.pageButton, value === page && s.pageButtonActive)} onClick={() => value !== page && onChange({ page: value })}>{value}</button>
                        )))}
                      <button type="button" className={s.pageButton} disabled={page >= pageCount} onClick={() => onChange({ page: page + 1 })}
                        aria-label={formatMessage(messages.orpNext)}>&rsaquo;</button>
                    </nav>
                  )}
                </div>
              </>
            )}
        </div>
        {selected && <EarningDetail row={selected} currency={currency} opener={opener} onClose={closeDetail} />}
      </div>
    );
  }

  return <div className={sh.page}>{header}{body}</div>;
};

export { PAGE_SIZE };
export default withStyles(sh, s)(EarningsPage);
